import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader } from '../ui/card';
import { Badge } from '../ui/badge';
import { ArrowRight, BookOpen, Code, Zap, Globe, Clock } from 'lucide-react';
import { blogData } from '../../mock/blogData';
import { useTranslation } from '../../hooks/useTranslation';

const IntegrationsSection = () => {
  const { t } = useTranslation();
  
  // Get translated articles
  const translatedArticles = t('integrations.articles');
  
  // Combine translated data with original blogData structure for compatibility
  const displayedIntegrations = blogData.articles.slice(0, 3).map((article, index) => ({
    ...article,
    title: translatedArticles[index]?.title || article.title,
    excerpt: translatedArticles[index]?.excerpt || article.excerpt,
    readTime: `${article.readTime.split(' ')[0]} ${t('integrations.readTime')}`
  }));

  return (
    <section className="py-24 bg-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center mb-6">
            <Code className="w-8 h-8 text-cyan-400 mr-3" />
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white">
              {t('integrations.title1')} <span className="text-cyan-400">{t('integrations.title2')}</span>
            </h2>
          </div>
          <p className="text-xl text-slate-400 max-w-3xl mx-auto mb-8">
            {t('integrations.subtitle')}
          </p>
          
          {/* Quick Stats */}
          <div className="flex flex-wrap items-center justify-center gap-8 mb-8">
            <div className="flex items-center text-slate-300">
              <BookOpen className="w-5 h-5 text-cyan-400 mr-2" />
              <span className="font-semibold">{blogData.articles.length}+ {t('integrations.tutorials')}</span>
            </div>
            <div className="flex items-center text-slate-300">
              <Globe className="w-5 h-5 text-green-400 mr-2" />
              <span className="font-semibold">{blogData.categories.length} {t('integrations.categories')}</span>
            </div>
            <div className="flex items-center text-slate-300">
              <Zap className="w-5 h-5 text-yellow-400 mr-2" />
              <span className="font-semibold">{t('integrations.readyInMinutes')}</span>
            </div>
          </div>
        </div>

        {/* Featured Integrations Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {displayedIntegrations.map((integration) => (
            <Link key={integration.id} to={`/integrations/${integration.slug}`}>
              <Card className="bg-slate-950/50 border-slate-800 hover:border-cyan-500/50 transition-all duration-300 h-full group overflow-hidden">
                <div className="aspect-video overflow-hidden">
                  <img 
                    src={integration.image} 
                    alt={integration.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between mb-3">
                    <Badge className="bg-cyan-500/10 text-cyan-400 border-cyan-500/20">
                      {blogData.categories.find(c => c.slug === integration.category)?.name}
                    </Badge>
                    {integration.featured && (
                      <Badge variant="outline" className="border-yellow-500/50 text-yellow-400">
                        {t('integrations.popular')}
                      </Badge>
                    )}
                  </div>
                  <h3 className="text-xl font-semibold text-white leading-tight group-hover:text-cyan-400 transition-colors duration-300">
                    {integration.title}
                  </h3>
                </CardHeader>
                <CardContent className="pt-0">
                  <p className="text-slate-400 mb-4 line-clamp-3">{integration.excerpt}</p>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center text-sm text-slate-500">
                      <Clock className="w-4 h-4 mr-1" />
                      <span>{integration.readTime}</span>
                    </div>
                    
                    <ArrowRight className="w-5 h-5 text-cyan-400 group-hover:translate-x-1 transition-transform duration-200" />
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>

        {/* Categories Preview */}
        <div className="mb-12">
          <h3 className="text-2xl font-bold text-white mb-8 text-center">{t('integrations.browseByCategory')}</h3>
          <div className="flex flex-wrap justify-center gap-4">
            {blogData.categories.slice(0, 4).map((category) => (
              <Link 
                key={category.slug} 
                to={`/integrations?category=${category.slug}`}
                className="group"
              >
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl px-6 py-4 hover:border-cyan-500/50 hover:bg-slate-800/70 transition-all duration-300">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-cyan-500 rounded-lg flex items-center justify-center">
                      <Code className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-white group-hover:text-cyan-400 transition-colors duration-200">
                        {category.name}
                      </h4>
                      <p className="text-sm text-slate-400">{category.count} {t('integrations.tutorials').toLowerCase()}</p>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center">
          <div className="bg-gradient-to-r from-slate-800/50 to-slate-900/50 border border-slate-700 rounded-2xl p-8">
            <h3 className="text-2xl font-bold text-white mb-4">
              {t('integrations.ctaTitle')}
            </h3>
            <p className="text-slate-400 mb-6 max-w-2xl mx-auto">
              {t('integrations.ctaSubtitle')}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
              <Link to="/integrations">
                <Button 
                  size="lg"
                  className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold px-8 py-3 group"
                >
                  <BookOpen className="w-5 h-5 mr-2" />
                  {t('integrations.browseAllTutorials')}
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform duration-200" />
                </Button>
              </Link>
              <Link to="/pricing">
                <Button 
                  variant="outline" 
                  size="lg"
                  className="border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white px-8 py-3"
                >
                  {t('integrations.viewPricing')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default IntegrationsSection;